import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'walletStep', 'listingForm', 'progressStep', 'connectButton', 'walletStatus',
    'fixedFields', 'auctionFields', 'fixedTab', 'auctionTab', 'submitButton', 'error', 'explorerLink',
    'loader', 'progressMessage'
  ]

  static values = {
    nftNumber: String,
    walletVerified: Boolean,
    verifiedAddress: String,
    nftContract: String,
    marketplaceContract: String,
    tokenId: String
  }

  connect () {
    this.syncSaleType()
    this.restoreWalletState()
  }

  async restoreWalletState () {
    if (!this.walletVerifiedValue || !globalThis.ethereum) return
    const accounts = await globalThis.ethereum.request({ method: 'eth_accounts' })
    if (accounts[0]?.toLowerCase() === this.verifiedAddressValue.toLowerCase()) this.showListingForm(accounts[0])
  }

  async connectWallet () {
    try {
      if (!globalThis.ethereum) throw new Error('Install MetaMask to sell this NFT.')
      await this.ensureSepolia()
      const accounts = await globalThis.ethereum.request({ method: 'eth_requestAccounts' })
      const address = accounts[0]?.toLowerCase()
      if (!address) throw new Error('No MetaMask account was selected.')

      const challenge = await this.requestJson('/nfts/wallet_challenge', { method: 'POST', body: { address } })
      const signature = await globalThis.ethereum.request({ method: 'personal_sign', params: [challenge.message, address] })
      await this.requestJson('/nfts/wallet_connect', { method: 'POST', body: { address, nonce: challenge.nonce, signature } })

      this.walletVerifiedValue = true
      this.verifiedAddressValue = address
      this.showListingForm(address)
    } catch (error) {
      this.showWalletError(error)
    }
  }

  changeSaleType () {
    this.syncSaleType()
  }

  syncSaleType () {
    const selectedType = this.listingFormTarget.querySelector('input[name="listing[sale_type]"]:checked')?.value
    const auction = selectedType === 'auction'
    this.fixedFieldsTarget.classList.toggle('d-none', auction)
    this.auctionFieldsTarget.classList.toggle('d-none', !auction)
    this.fixedTabTarget.classList.toggle('active', !auction)
    this.auctionTabTarget.classList.toggle('active', auction)
    this.fixedFieldsTarget.querySelectorAll('input').forEach((input) => {
      input.disabled = auction
      input.required = !auction
    })
    this.auctionFieldsTarget.querySelectorAll('input').forEach((input) => {
      input.disabled = !auction
      input.required = auction
    })
  }

  async submitListing (event) {
    event.preventDefault()
    try {
      if (!this.nftContractValue) throw new Error('NFT contract is not configured.')
      await this.ensureActiveWallet()
      this.showProgress()

      const listing = this.listingAttributes()
      const prepared = await this.requestJson(`/nfts/${this.nftNumberValue}/prepare_listing`, {
        method: 'POST',
        body: { listing }
      })
      this.currentTokenId = prepared.token_id || this.tokenIdValue
      this.setStage('approval', 'active')
      const approvalTxHash = await this.approveMarketplace(prepared.nft_contract_address)
      this.setStage('approval', 'complete')
      this.setStage('listing', 'active')
      const listingResult = await this.createMarketplaceListing(listing)
      this.setStage('listing', 'complete')
      this.setStage('confirm', 'active')
      const onChainListing = await this.requestJson(`/nfts/${this.nftNumberValue}/confirm_on_chain_listing`, {
        method: 'PATCH',
        body: {
          listing_id: prepared.listing_id,
          approval_tx_hash: approvalTxHash,
          listing_tx_hash: listingResult.txHash
        }
      })
      this.setStage('confirm', 'complete')
      this.explorerLinkTarget.href = onChainListing.explorer_url
      this.explorerLinkTarget.classList.remove('d-none')
      this.progressMessageTarget.textContent = `Listed on Sepolia as #${onChainListing.chain_listing_id}`
      this.hideLoader()
    } catch (error) {
      this.showError(error)
    }
  }

  async approveMarketplace (contractAddress) {
    const accounts = await globalThis.ethereum.request({ method: 'eth_requestAccounts' })
    const txHash = await globalThis.ethereum.request({
      method: 'eth_sendTransaction',
      params: [{
        from: accounts[0],
        to: contractAddress,
        data: `0xa22cb465${this.addressWord(this.marketplaceContractValue)}${this.uintWord(1)}`
      }]
    })
    const receipt = await this.waitForReceipt(txHash)
    if (BigInt(receipt.status || '0x0') !== 1n) throw new Error('Marketplace approval transaction reverted.')
    return txHash
  }

  async createMarketplaceListing (listing) {
    const accounts = await globalThis.ethereum.request({ method: 'eth_requestAccounts' })
    const fixedPrice = listing.sale_type === 'fixed_price'
    const amount = this.parseEther(fixedPrice ? listing.price_eth : listing.reserve_price_eth)
    const data = fixedPrice
      ? `0x1b4d7fa3${this.addressWord(this.nftContractValue)}${this.uintWord(this.currentTokenId)}${this.uintWord(amount)}`
      : `0xc9944ccf${this.addressWord(this.nftContractValue)}${this.uintWord(this.currentTokenId)}${this.uintWord(amount)}${this.uintWord(this.timestamp(listing.starts_at))}${this.uintWord(this.timestamp(listing.ends_at))}`
    const txHash = await globalThis.ethereum.request({
      method: 'eth_sendTransaction',
      params: [{ from: accounts[0], to: this.marketplaceContractValue, data }]
    })
    const receipt = await this.waitForReceipt(txHash)
    if (BigInt(receipt.status || '0x0') !== 1n) throw new Error('Marketplace listing transaction reverted.')
    return { txHash, chainListingId: this.listingIdFromReceipt(receipt) }
  }

  async ensureActiveWallet () {
    await this.ensureSepolia()
    const accounts = await globalThis.ethereum.request({ method: 'eth_requestAccounts' })
    if (accounts[0]?.toLowerCase() !== this.verifiedAddressValue.toLowerCase()) {
      throw new Error('The active MetaMask account does not match the verified seller wallet.')
    }
  }

  async ensureSepolia () {
    const chainId = await globalThis.ethereum.request({ method: 'eth_chainId' })
    if (chainId.toLowerCase() !== '0xaa36a7') {
      await globalThis.ethereum.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: '0xaa36a7' }] })
    }
  }

  async waitForReceipt (txHash) {
    const deadline = Date.now() + 180000
    while (Date.now() < deadline) {
      const receipt = await globalThis.ethereum.request({ method: 'eth_getTransactionReceipt', params: [txHash] })
      if (receipt) return receipt
      await new Promise((resolve) => setTimeout(resolve, 2500))
    }
    throw new Error('Marketplace transaction is still pending. Check Sepolia Etherscan.')
  }

  listingIdFromReceipt (receipt) {
    const topic = '0x409c1733b50d22fd8d76c9d725ae381e2394bff146f4210d54e5bb76b7932064'
    const event = receipt.logs.find((log) =>
      log.address.toLowerCase() === this.marketplaceContractValue.toLowerCase() &&
      log.topics?.[0]?.toLowerCase() === topic
    )
    if (!event?.topics?.[1]) throw new Error('Marketplace listing event was not found.')
    return BigInt(event.topics[1]).toString()
  }

  addressWord (address) {
    return address.toLowerCase().replace(/^0x/, '').padStart(64, '0')
  }

  uintWord (value) {
    return BigInt(value).toString(16).padStart(64, '0')
  }

  parseEther (value) {
    const [whole = '0', fraction = ''] = value.toString().split('.')
    return BigInt(whole) * (10n ** 18n) + BigInt(fraction.padEnd(18, '0').slice(0, 18) || '0')
  }

  timestamp (value) {
    const milliseconds = Date.parse(value)
    if (!Number.isFinite(milliseconds)) throw new Error('Enter valid auction dates.')
    return BigInt(Math.floor(milliseconds / 1000))
  }

  async requestJson (path, options) {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 120000)
    let response
    try {
      response = await fetch(path, {
        method: options.method,
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': document.querySelector("[name='csrf-token']").content },
        body: options.body ? JSON.stringify(options.body) : undefined,
        signal: controller.signal
      })
    } catch (error) {
      if (error.name === 'AbortError') throw new Error('The server took too long to respond. Please try again.')
      throw error
    } finally {
      clearTimeout(timeout)
    }
    const responseText = await response.text()
    let payload = {}
    if (responseText.trim()) {
      try {
        payload = JSON.parse(responseText)
      } catch (_error) {
        throw new Error(`Server returned an invalid response (${response.status}).`)
      }
    }
    if (!response.ok) throw new Error(payload.errors?.join(', ') || payload.error || 'Request failed.')
    return payload
  }

  listingAttributes () {
    return Object.fromEntries(
      Array.from(new FormData(this.listingFormTarget).entries(), ([name, value]) => {
        const match = name.match(/^listing\[(.+)\]$/)
        return [match ? match[1] : name, value]
      })
    )
  }

  showListingForm (address) {
    this.walletStepTarget.classList.add('d-none')
    this.listingFormTarget.classList.remove('d-none')
    this.walletStatusTarget.textContent = `${this.short(address)} · Sepolia`
  }

  showProgress () {
    this.listingFormTarget.classList.add('d-none')
    this.progressStepTarget.classList.remove('d-none')
    this.errorTarget.classList.add('d-none')
    this.showLoader('Preparing your NFT listing...')
  }

  setStage (name, state, label = null) {
    const stage = this.progressStepTarget.querySelector(`[data-stage="${name}"]`)
    stage.dataset.status = state
    if (label) {
      const stageLabel = stage.querySelector('[data-stage-label]')
      stageLabel.textContent = `${stageLabel.textContent.split(' — ')[0]} — ${label}`
    }
    if (state === 'active') this.showLoader(this.stageMessage(name))
    if (state === 'blocked') this.hideLoader()
  }

  showError (error) {
    this.progressStepTarget.classList.remove('d-none')
    this.progressStepTarget.querySelector('[data-status="active"]')?.setAttribute('data-status', 'error')
    this.hideLoader()
    this.errorTarget.textContent = error.message || 'NFT marketplace request failed.'
    this.errorTarget.classList.remove('d-none')
  }

  showWalletError (error) {
    if (!globalThis.ethereum) return this.showMetaMaskInstallFlash('sell')
    return this.showFlash(error.message || 'Wallet connection failed.', 'error')
  }

  showFlash (message, icon) {
    return globalThis.Swal.fire({
      customClass: {
        popup: 'swal-popup alert-sm-bg align-items-center justify-content-center w-100',
        htmlContainer: 'text-black-paint text-white'
      },
      animation: false,
      html: message,
      icon,
      showCloseButton: true,
      buttonsStyling: false,
      showConfirmButton: false,
      timer: 3000
    })
  }

  showMetaMaskInstallFlash (action) {
    return globalThis.Swal.fire({
      customClass: {
        popup: 'nft-metamask-install-popup',
        title: 'nft-metamask-install-title',
        htmlContainer: 'nft-metamask-install-body',
        confirmButton: 'nft-metamask-install-close'
      },
      animation: false,
      title: 'MetaMask required',
      html: `
        <div class="nft-metamask-install-content">
          <span class="nft-metamask-install-kicker">Ethereum Sepolia · ERC-721</span>
          <p class="nft-metamask-install-lead">You need MetaMask to ${action} this NFT on Sepolia.</p>
          <ol class="nft-metamask-install-steps">
            <li>Open the official MetaMask download page.</li>
            <li>Add the extension to your browser.</li>
            <li>Create or import a wallet.</li>
            <li>Refresh this page and click Metamask again.</li>
          </ol>
          <a class="nft-metamask-install-link" href="https://metamask.io/download/" target="_blank" rel="noopener noreferrer">Open MetaMask Install Page</a>
        </div>
      `,
      buttonsStyling: false,
      showCloseButton: true,
      confirmButtonText: 'OK'
    })
  }

  showLoader (message) {
    this.progressMessageTarget.textContent = message
    this.loaderTarget.classList.remove('d-none')
  }

  hideLoader () {
    this.loaderTarget.classList.add('d-none')
  }

  stageMessage (name) {
    return {
      approval: 'Confirm marketplace approval in MetaMask...',
      listing: 'Confirm the listing transaction in MetaMask...',
      confirm: 'Verifying listing and NFT escrow on Sepolia...'
    }[name]
  }

  short (address) {
    return `${address.slice(0, 8)}…${address.slice(-6)}`
  }
}
